import { Collection, Database, Q, Query } from '@nozbe/watermelondb';
import { Where } from '@nozbe/watermelondb/QueryDescription';

import Horse from './Horse';
import HorseContact from './HorseContact';
import Organisation from './Organisation';
import {
    DBServiceOptionsWithImagesAndValidation,
    DBServiceOptionsWithImagesLoggerAndValidation,
    EntryDBServiceOptions,
} from '../../types/dbService';
import {
    EntryModel,
    EntryPayload,
    EntryProcedureToAdd,
    EntryProductToAdd,
    PreparedDataOnEntryAdd,
    PreparedDataOnEntryDelete,
    PreparedDataOnEntryUpdate,
} from '../../types/Entries';
import { EntryProcedureModel } from '../../types/EntryProcedure';
import { EntryProductModel } from '../../types/EntryProduct';
import {
    getMatchingElementsFromTwoArrays,
    getUniqueElementsFromTwoArrays,
} from '../../utils/array';
import {
    applyEntriesFiltersQueries,
    applyEntriesSearchQueries,
} from '../utils';
import { getLocal } from '../../utils/date';
import { EntryQueryOptions } from '../../types/entriesFilters';
import { ContactModel } from '../../types/Contacts';
import { HorseModel } from '../../types/Horses';
import { FILTER_TYPE } from '../../types/filter';
import { Photo } from '../../types/Photo';
import { DatabaseValidation } from '../../types/validation';
import { addRelatedDataBatch } from './helpers/entry/common';
import {
    appendPreparedEntryProcedures as appendPreparedEntryProceduresOnAdd,
    appendPreparedEntryProducts as appendPreparedEntryProductsOnAdd,
    appendPreparedEntryUsers as appendPreparedEntryUsersOnAdd,
    logDBActions,
    prepareCreateEntry,
    prepareEntryProcedures,
    prepareEntryProducts,
    prepareEntryUsers,
} from './helpers/entry/entryAdd';
import { validateEntityRecords } from './helpers/common/validateEntityRecords';
import {
    appendPreparedEntries,
    logBumpDBActions,
    prepareBumpEntries,
    prepareBumpEntryUpdates,
} from './helpers/entry/entryBump';
import {
    appendPreparedEntryProcedures,
    appendPreparedEntryProducts,
    appendPreparedEntryUsers,
    logUpdateDBActions,
    prepareRevertInventoryChangeForEntryProduct,
    prepareUpdateEntry,
    prepareUpdateEntryProcedures,
    prepareUpdateEntryProducts,
    prepareUpdateEntryUsers,
} from './helpers/entry/entryUpdate';
import {
    appendPreparedDeleteEntry,
    appendPreparedDeleteEntryProcedures,
    appendPreparedDeleteEntryProducts,
    appendPreparedDeleteEntryUsers,
    appendPreparedInventoryChanges,
    logDeleteDBActions,
    prepareDeleteEntryById,
    prepareDeleteEntryRelatedData,
} from './helpers/entry/entryDelete';

class Entry {
    private database: Database;
    private collection: Collection<EntryModel>;
    private table = 'entries';
    private options: EntryDBServiceOptions;
    private validation: DatabaseValidation;

    constructor(options: EntryDBServiceOptions) {
        this.database = options.database;
        this.collection = options.database.collections.get(this.table);
        this.options = options;
        this.validation = options.validation;
    }

    getAll() {
        return this.collection.query().fetch();
    }

    getCount() {
        return this.collection.query().fetchCount();
    }

    getByID(id: string) {
        return this.collection.find(id);
    }

    observeCount() {
        return this.collection.query().observeCount();
    }

    async getByParam(param: string, value: any) {
        return this.collection.query(Q.where(param, value)).fetch();
    }

    async getByParams(params = {}) {
        if (Object.keys(params).length <= 0) {
            return [];
        }

        const whereConditions: Where[] = [];
        Object.entries(params).forEach(([param, value]) => {
            // @ts-ignore
            whereConditions.push(Q.where(param, value));
        });

        return this.collection.query(...whereConditions).fetch();
    }

    async getNewestEntryByHorseID(horseID: string) {
        const [lastEntry] = await this.collection
            .query(
                Q.where('horse_id', horseID),
                Q.sortBy('logged_time', 'desc'),
                Q.take(1),
            )
            .fetch();

        return lastEntry;
    }

    getEntriesFilterQuery(options: EntryQueryOptions): [Q.Clause[], Q.Clause] {
        const { filters, invoiceOrder } = options;

        const queries: Q.Clause[] = [];

        if (filters) {
            queries.push(
                Q.experimentalJoinTables(['invoices']),
                ...applyEntriesFiltersQueries(filters),
            );
        }

        return [
            queries,
            invoiceOrder
                ? Q.sortBy('invoice_id', undefined)
                : Q.sortBy('logged_time', 'desc'),
        ];
    }

    getEntriesSearchLokiQuery(searchText: string) {
        const parsedSearchText = searchText.toLowerCase().trim();

        return applyEntriesSearchQueries(parsedSearchText);
    }

    getEntriesSearchSQLQuery(searchText: string) {
        const parsedSearchText = searchText.toLowerCase().trim();

        const sanitizedSearch = Q.sanitizeLikeString(parsedSearchText);

        if (!sanitizedSearch.length) return;

        return [
            Q.experimentalJoinTables(['horses']),
            Q.experimentalNestedJoin('horses', 'horse_contacts'),
            Q.experimentalNestedJoin('horse_contacts', 'contacts'),
            Q.or(
                Q.on(
                    'horses',
                    Q.or(
                        Q.where('name', Q.like(`${sanitizedSearch}%`)),
                        Q.where('name', Q.like(`% ${sanitizedSearch}%`)),
                    ),
                ),
                Q.on(
                    'horses',
                    Q.on(
                        'horse_contacts',
                        Q.on(
                            'contacts',
                            Q.or(
                                // @ts-ignore
                                Q.unsafeSqlExpr(
                                    `first_name || ' ' || last_name LIKE '${sanitizedSearch}%'`,
                                ),
                                Q.where(
                                    'last_name',
                                    Q.like(`${sanitizedSearch}%`),
                                ),
                            ),
                        ),
                    ),
                ),
            ),
        ];
    }

    filterEntries(options: EntryQueryOptions) {
        const [filterQueries, sortQuery] = this.getEntriesFilterQuery(options);

        const searchQuery =
            options.filterType === FILTER_TYPE.LOKI
                ? this.getEntriesSearchLokiQuery(options.searchText)
                : this.getEntriesSearchSQLQuery(options.searchText);

        if (searchQuery) filterQueries.push(...searchQuery);

        return this.collection.query(...filterQueries).extend(sortQuery);
    }

    getFilteredEntriesCount(options: EntryQueryOptions) {
        const query = this.filterEntries(options);

        return query.fetchCount();
    }

    getFilteredEntries(options: EntryQueryOptions) {
        const query = this.filterEntries(options);

        return query.fetch();
    }

    // Add related products to the entry and update inventory
    async addEntryProducts(
        entry: EntryModel,
        entryProducts: (EntryProductToAdd | EntryProductModel)[],
        userId: string,
    ) {
        const { entryProductsToAdd, inventoryProductsToAdd, inventoryChanges } =
            await prepareEntryProducts({
                createdEntry: entry,
                entryProducts,
                userId,
                serviceOptions: {
                    database: this.database,
                    imageService: this.options.imageService,
                    logDBAction: this.options.logger.logDBAction,
                    validation: this.validation,
                },
                database: this.database,
            });

        return {
            inventoryProductsToAdd,
            entryProductsToAdd,
            inventoryChanges,
        };
    }

    // Add related procedures to the entry
    async addEntryProcedures(
        entry: EntryModel,
        entryProcedures: (EntryProcedureToAdd | EntryProcedureModel)[],
    ) {
        const { entryProceduresToAdd } = await prepareEntryProcedures({
            createdEntry: entry,
            entryProcedures,
            serviceOptions: {
                database: this.database,
                imageService: this.options.imageService,
                logDBAction: this.options.logger.logDBAction,
                validation: this.validation,
            },
            database: this.database,
        });

        return entryProceduresToAdd;
    }

    async addEntryImage(
        entryId: string,
        image: Photo,
        userId: string,
    ): Promise<void> {
        const organisationService = new Organisation({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.validation,
        });
        const organisation = await organisationService.get();
        const { id: organisationID } = organisation[0];

        await this.options.imageService.uploadImage({
            uploadInBackground: true,
            image,
            entityID: entryId,
            entityType: 'Entry',
            annotationImage: '',
            ownerID: userId,
            userIDs: [userId],
            shouldUploadImage: true,
            organisationID,
        });
    }

    async add(payload: EntryPayload, userId: string) {
        const serviceOptions: DBServiceOptionsWithImagesAndValidation = {
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.validation,
        };
        const serviceOptionsWithLogger: DBServiceOptionsWithImagesLoggerAndValidation =
            {
                database: this.database,
                imageService: this.options.imageService,
                logger: this.options.logger,
                validation: this.validation,
            };

        let createdEntry: EntryModel | undefined;

        createdEntry = await this.database.write(async () => {
            let preparedData: PreparedDataOnEntryAdd[] = [];

            const entry = await prepareCreateEntry({
                payload,
                userId,
                serviceOptions,
                collection: this.collection,
            });
            preparedData.push({
                record: entry,
                table: 'entries',
                operation: 'add',
            });

            const productsData = await prepareEntryProducts({
                createdEntry: entry,
                entryProducts: payload.entryProducts,
                userId,
                serviceOptions,
                database: this.database,
            });
            preparedData = appendPreparedEntryProductsOnAdd(
                preparedData,
                productsData,
            );

            const proceduresData = await prepareEntryProcedures({
                createdEntry: entry,
                entryProcedures: payload.entryProcedures,
                serviceOptions,
                database: this.database,
            });
            preparedData = appendPreparedEntryProceduresOnAdd(
                preparedData,
                proceduresData,
            );

            let usersData:
                | Awaited<ReturnType<typeof prepareEntryUsers>>
                | undefined;
            if (payload.membersIds) {
                usersData = await prepareEntryUsers({
                    createdEntry: entry,
                    membersIds: payload.membersIds,
                    serviceOptions: serviceOptionsWithLogger,
                });
                preparedData = appendPreparedEntryUsersOnAdd(
                    preparedData,
                    usersData,
                );
            }

            validateEntityRecords(
                preparedData.filter((r) => r.record != null),
                this.validation,
            );

            await addRelatedDataBatch(
                preparedData.map((item) => item.record),
                this.database,
            );

            logDBActions({
                createdEntry: entry,
                productsData,
                proceduresData,
                usersData,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });

            return entry;
        }, 'create-entry');

        return createdEntry;
    }

    async update(id: string, payload: EntryPayload, userId: string) {
        const serviceOptions: DBServiceOptionsWithImagesAndValidation = {
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.validation,
        };
        const serviceOptionsWithLogger: DBServiceOptionsWithImagesLoggerAndValidation =
            {
                database: this.database,
                imageService: this.options.imageService,
                logger: this.options.logger,
                validation: this.validation,
            };
        const entryElement = await this.getByID(id);
        const initialLoggedTime = entryElement.loggedTime;

        const { entryProcedures, entryProducts, imagesToDelete, membersIds } =
            payload;

        const updatedEntry = await this.database.write(async () => {
            const initialEntryProcedures =
                await entryElement.entryProcedures.fetch();
            const initialEntryProducts =
                await entryElement.entryProducts.fetch();

            const proceduresData = await prepareUpdateEntryProcedures({
                entryElement,
                entryProcedures,
                initialEntryProcedures,
                serviceOptions,
                database: this.database,
            });

            const productsData = await prepareUpdateEntryProducts({
                entryElement,
                entryProducts,
                initialEntryProducts,
                initialLoggedTime,
                loggedTime: payload.loggedTime,
                userId,
                serviceOptions,
                database: this.database,
            });

            let usersData:
                | Awaited<ReturnType<typeof prepareUpdateEntryUsers>>
                | undefined;
            if (membersIds) {
                usersData = await prepareUpdateEntryUsers({
                    entryId: id,
                    membersIds,
                    serviceOptions: serviceOptionsWithLogger,
                });
            }

            const updatedEntryElement = prepareUpdateEntry({
                entryElement,
                payload,
            });

            let preparedData: PreparedDataOnEntryUpdate[] = [
                {
                    record: updatedEntryElement,
                    table: 'entries',
                    operation: 'update',
                },
            ];
            preparedData = appendPreparedEntryProcedures(
                preparedData,
                proceduresData,
            );
            preparedData = appendPreparedEntryProducts(
                preparedData,
                productsData,
            );
            if (usersData) {
                preparedData = appendPreparedEntryUsers(
                    preparedData,
                    usersData,
                );
            }

            validateEntityRecords(
                preparedData.filter((r) => r.record != null),
                this.validation,
            );

            await addRelatedDataBatch(
                preparedData.map((item) => item.record),
                this.database,
            );

            logUpdateDBActions({
                entryElement,
                buckets: {
                    ...proceduresData,
                    ...productsData,
                    entryUsersToAdd: usersData?.entryUsersToAdd ?? [],
                    entryUsersToDelete: usersData?.entryUsersToDelete ?? [],
                },
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });

            return updatedEntryElement;
        }, 'update-entry');

        if (imagesToDelete && imagesToDelete.length > 0) {
            imagesToDelete.map(async (docID) => {
                await this.options.imageService.removeByDocId(docID);
            });
        }

        return updatedEntry;
    }

    async prepareBumpUpdates(ids: string[]) {
        const entryElements = await Promise.all(
            ids.map(async (id) => this.getByID(id)),
        );

        return prepareBumpEntryUpdates(entryElements);
    }

    async bump(ids: string[]) {
        await this.database.write(async () => {
            const { entryElements } = await prepareBumpEntries({
                ids,
                getByID: (entryId) => this.getByID(entryId),
            });
            const entriesToUpdate = prepareBumpEntryUpdates(entryElements);

            let preparedData: PreparedDataOnEntryUpdate[] = [];
            preparedData = appendPreparedEntries(preparedData, {
                entriesToUpdate,
            });

            validateEntityRecords(
                preparedData.filter((r) => r.record != null),
                this.validation,
            );

            await addRelatedDataBatch(
                preparedData.map((item) => item.record),
                this.database,
            );

            logBumpDBActions({
                entriesToUpdate,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });
        });
    }

    async deleteByID(id: string, userId: string) {
        const serviceOptions: DBServiceOptionsWithImagesLoggerAndValidation = {
            database: this.database,
            imageService: this.options.imageService,
            logger: this.options.logger,
            validation: this.validation,
        };

        await this.database.write(async () => {
            const entryElement = await this.getByID(id);
            let preparedData: PreparedDataOnEntryDelete[] = [];

            const deleteData = await prepareDeleteEntryRelatedData({
                id,
                userId,
                entryElement,
                serviceOptions,
            });

            preparedData = appendPreparedInventoryChanges(
                preparedData,
                deleteData,
            );
            preparedData = appendPreparedDeleteEntryProcedures(
                preparedData,
                deleteData,
            );
            preparedData = appendPreparedDeleteEntryProducts(
                preparedData,
                deleteData,
            );
            preparedData = appendPreparedDeleteEntryUsers(
                preparedData,
                deleteData,
            );
            preparedData = appendPreparedDeleteEntry(preparedData, deleteData);

            validateEntityRecords(
                preparedData.filter((r) => r.record != null),
                this.validation,
            );

            await addRelatedDataBatch(
                preparedData.map((item) => item.record),
                this.database,
            );

            logDeleteDBActions({
                deleteData,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });
        }, 'delete-entry');

        this.options.imageService.remove(id);
    }

    async prepareDeleteById(id: string, userId: string) {
        const serviceOptions: DBServiceOptionsWithImagesAndValidation = {
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.validation,
        };

        const entryElement = await this.getByID(id);

        return prepareDeleteEntryById({
            entryElement,
            userId,
            serviceOptions,
        });
    }

    async prepareRevertInventoryChangeForEntryProduct(
        entryProduct: EntryProductModel,
        oldLoggedTime: string,
        newLoggedTime: string,
        userId: string,
    ) {
        const serviceOptions: DBServiceOptionsWithImagesAndValidation = {
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.validation,
        };

        return prepareRevertInventoryChangeForEntryProduct({
            entryProduct,
            oldLoggedTime,
            newLoggedTime,
            userId,
            serviceOptions,
        });
    }

    async getEntriesWithoutHorseContact() {
        const horseService = new Horse({
            database: this.database,
            imageService: this.options.imageService,
            logger: this.options.logger,
            validation: this.validation,
        });
        const horseContactService = new HorseContact({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.validation,
        });

        const allHorses = await horseService.getAll();
        const allHorseContacts = await horseContactService.getAll();
        const allEntries = await this.getAll();

        const horsesWithoutContacts = getUniqueElementsFromTwoArrays(
            allHorses,
            allHorseContacts,
            'id',
            'horseId',
        );

        return getMatchingElementsFromTwoArrays(
            allEntries,
            horsesWithoutContacts,
            'horseId',
            'id',
        );
    }

    getEntriesWithoutHorses() {
        return this.collection.query(Q.where('horse_id', null)).fetch();
    }

    getEntriesForHorsesQuery(horses: HorseModel[]): Query<EntryModel> {
        const horsesIds = horses.map((horse) => horse.id);

        const query = Q.or(
            ...horsesIds.map((id) => Q.where('horse_id', id)),
            Q.where('horse_id', null),
        );

        return this.collection.query(query);
    }

    async getEntriesForContact(contactId: string) {
        const contacts = await this.database.collections
            .get<ContactModel>('contacts')
            .query(Q.where('id', contactId));
        const horses = await contacts[0].horses.fetch();
        const query = this.getEntriesForHorsesQuery(horses);
        return query.fetch();
    }

    getEntriesByHorseName = (horsesIds: string[], name: string) => {
        return this.collection.query(
            Q.on(
                'horses',
                Q.and(
                    Q.where('id', Q.oneOf(horsesIds)),
                    Q.where('name', Q.like(`%${Q.sanitizeLikeString(name)}%`)),
                ),
            ),
        );
    };

    getEntriesIncludingHorseName = (horsesIds: string[], name: string) => {
        return this.collection.query(
            Q.on(
                'horses',
                Q.and(
                    Q.where('id', Q.oneOf(horsesIds)),
                    Q.where('name', Q.like(`%${Q.sanitizeLikeString(name)}%`)),
                ),
            ),
        );
    };

    getEntriesByContactName = (name: string) => {
        return this.collection.query(
            Q.experimentalJoinTables(['horses']),
            Q.experimentalNestedJoin('horses', 'horse_contacts'),
            Q.experimentalNestedJoin('horse_contacts', 'contacts'),
            Q.unsafeSqlQuery(
                'select entries.* from entries ' +
                    'left join horses on entries.horse_id is horses.id ' +
                    'left join horse_contacts on horses.id is horse_contacts.horse_id ' +
                    'left join contacts on horse_contacts.contact_id is contacts.id ' +
                    `where contacts.first_name || ' ' || contacts.last_name like '%${Q.sanitizeLikeString(
                        name,
                    )}%' or contacts.first_name like '%${Q.sanitizeLikeString(
                        name,
                    )}%' or contacts.last_name like '%${Q.sanitizeLikeString(
                        name,
                    )}%' and contacts._status is not 'deleted'
                        and horse_contacts._status is not 'deleted'
                        and horses._status is not 'deleted'
                        and entries._status is not 'deleted'`,
            ),
        );
    };

    getEntriesToInvoiceCount() {
        const today = getLocal();

        const tomorrow = today.plus({ days: 1 });
        const formattedTomorrow = Q.sanitizeLikeString(
            tomorrow.toISO().split('T')[0],
        ).replace(/_/g, '-');

        return this.collection
            .query(
                Q.or(Q.where('invoice_id', ''), Q.where('invoice_id', null)),
                Q.where('logged_time', Q.lt(formattedTomorrow)),
            )
            .observeCount();
    }

    getRecentlyUpdatedEntries(limit = 5) {
        return this.collection.query(
            Q.sortBy('updated_at', Q.desc),
            Q.take(limit),
        );
    }
}

export default Entry;
